import { useTranslation } from '@fluentsms/i18n';
import { Card, CardContent, CardHeader, Divider, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { Comment as CommentIcon } from '@material-ui/icons';
import React, { PropsWithChildren, ReactNode } from 'react';

interface Props {
  author: ReactNode;
  updatedAt?: string;
  headerAction?: ReactNode;
  id?: string;
}

export const useStyles = makeStyles((theme: Theme) => ({
  commentCard: {
    flexGrow: 1,
  },
  commentCardHeader: {
    padding: theme.spacing(1.5, 2, 1, 2),
    backgroundColor: theme.palette.brand.feather,
    color: theme.palette.text.secondary,
  },
  commentCardTitle: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.brand.feather,
    color: theme.palette.text.secondary,
  },
  commentCardContent: {
    ...theme.typography.body2,
    whiteSpace: 'pre-wrap',
    minHeight: theme.spacing(3),
    color: theme.palette.secondary.dark,
    '& > *': {
      margin: 0,
    },
  },
}));

export function CommentCard({ author, updatedAt, headerAction = null, children, id = '' }: PropsWithChildren<Props>) {
  const classes = useStyles();
  const { t } = useTranslation('notes');

  const timestamp = updatedAt
    ? {
        day: new Intl.DateTimeFormat('en-us').format(new Date(updatedAt)),
        hour: new Intl.DateTimeFormat('en-us', {
          hour: 'numeric',
          minute: 'numeric',
        }).format(new Date(updatedAt)),
      }
    : null;

  return (
    <Card className={classes.commentCard} data-commentid={id}>
      <CardHeader
        className={classes.commentCardHeader}
        title={
          <Grid container wrap="nowrap" alignItems="center" spacing={1} className={classes.commentCardTitle}>
            <Grid item>
              <CommentIcon className={classes.commentCardTitle} fontSize="small" color="inherit" />
            </Grid>
            <Grid item>
              <Typography color="inherit">
                <strong>{author === 'SYSTEM' ? t('notes.systemUser') : author}</strong>
                {timestamp && (
                  <span>
                    {' '}
                    on {timestamp.day} @ {timestamp.hour}
                  </span>
                )}
              </Typography>
            </Grid>
          </Grid>
        }
        action={author === 'SYSTEM' ? null : headerAction}
        disableTypography={true}
      />
      <Divider />
      <CardContent className={classes.commentCardContent}>{children}</CardContent>
    </Card>
  );
}
